import { Button, Card, Col, Form, Input, Row, Select, message } from "antd";
import { useEffect } from "react";
import { getFirstItem } from "common/utils";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import {
  useGetClientPortalConfigurationsQuery,
  useGetConsumerPortalConfigurationsQuery,
  useGetPaymentPortalConfigurationsQuery,
  useUpdateClientPortalConfigurationsMutation,
  useUpdateDebtorPortalConfigurationsMutation,
  useUpdatePaymentPortalConfigurationsMutation,
} from "features/portalConfigurations/portalConfigurationsAPI";
import {
  useFetchAgencyQuery,
  useUpdateAgencyMutation,
} from "features/basicInfo/agencyPortal/agencySettingsAPI";
import styled from "styled-components";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";

const { TextArea } = Input;

const StyledCard = styled(Card)`
  margin-bottom: 16px;
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 16px;
`;

const StyledSaveButtonContainer = styled.div`
  margin-top: 16px;
  text-align: right;
`;

const stripProtocol = (value) => (value ? value.replace(/^https?:\/\//i, "") : value);

function PortalConfigurations() {
  const { data: constants = {} } = useFetchBackendConstantsQuery();
  const [form] = Form.useForm();
  const [updateDebtorPortalConfigurations, { isLoading: isDebtorPortalConfigurationsLoading }] =
    useUpdateDebtorPortalConfigurationsMutation();
  const [updateClientPortalConfigurations, { isLoading: isClientPortalConfigurationsLoading }] =
    useUpdateClientPortalConfigurationsMutation();
  const [updatePaymentPortalConfigurations, { isLoading: isPaymentPortalConfigurationsLoading }] =
    useUpdatePaymentPortalConfigurationsMutation();
  const [updateAgency, { isLoading: isAgencySettingsLoading }] = useUpdateAgencyMutation();
  const { data: debtorPortalConfigurations } = useGetConsumerPortalConfigurationsQuery();
  const { data: clientPortalConfigurations } = useGetClientPortalConfigurationsQuery();
  const { data: paymentPortalConfigurations } = useGetPaymentPortalConfigurationsQuery();
  const { data: agencySettings } = useFetchAgencyQuery();
  const {
    data: {
      [PERMISSIONS.PORTAL_CONFIGURATION__UPDATE]: isPortalConfigurationsAuthorized,
      [PERMISSIONS.AGENCY__UPDATE]: isAgencyUpdateAuthorized,
    },
  } = useAuthorizations();

  const debtorPortalConfiguration = getFirstItem(debtorPortalConfigurations);
  const clientPortalConfiguration = getFirstItem(clientPortalConfigurations);
  const paymentPortalConfiguration = getFirstItem(paymentPortalConfigurations);

  useEffect(() => {
    form.setFieldsValue({
      supportedPaymentMethods: debtorPortalConfiguration?.supportedPaymentMethods || [],
      signupDisclosure: debtorPortalConfiguration?.signupDisclosure,
      loginDisclosure: debtorPortalConfiguration?.loginDisclosure,
      convenienceFeeDisclosure: debtorPortalConfiguration?.convenienceFeeDisclosure,
      paymentPortalUrl: stripProtocol(debtorPortalConfiguration?.paymentPortalUrl),
      trainingSiteUrl: stripProtocol(clientPortalConfiguration?.trainingSiteUrl),
      paymentVerificationMethods: paymentPortalConfiguration?.paymentVerificationMethods,
      footerDisclosure1Text: debtorPortalConfiguration?.footerDisclosure1Text,
      footerDisclosure2Text: debtorPortalConfiguration?.footerDisclosure2Text,
      footerDisclosure3Text: debtorPortalConfiguration?.footerDisclosure3Text,
      footerDisclosure1Url: stripProtocol(debtorPortalConfiguration?.footerDisclosure1Url),
      footerDisclosure2Url: stripProtocol(debtorPortalConfiguration?.footerDisclosure2Url),
      footerDisclosure3Url: stripProtocol(debtorPortalConfiguration?.footerDisclosure3Url),
      paymentDisclosureNotice: agencySettings?.paymentDisclosureNotice,
    });
  }, [
    agencySettings,
    clientPortalConfiguration,
    debtorPortalConfiguration,
    form,
    paymentPortalConfiguration,
  ]);

  const handleConsumerPortalSave = async () => {
    let values;
    try {
      values = await form.validateFields([
        // Scope the validation to only the fields related to consumer portal to avoid unnecessary validation errors
        // when saving other portal configurations
        "supportedPaymentMethods",
        "signupDisclosure",
        "loginDisclosure",
        "convenienceFeeDisclosure",
        "paymentPortalUrl",
        "footerDisclosure1Text",
        "footerDisclosure2Text",
        "footerDisclosure3Text",
        "footerDisclosure1Url",
        "footerDisclosure2Url",
        "footerDisclosure3Url",
      ]);
    } catch (error) {
      return;
    }
    const result = await updateDebtorPortalConfigurations({
      supportedPaymentMethods: values.supportedPaymentMethods,
      signupDisclosure: values.signupDisclosure,
      loginDisclosure: values.loginDisclosure,
      convenienceFeeDisclosure: values.convenienceFeeDisclosure,
      paymentPortalUrl: values.paymentPortalUrl ? `https://${values.paymentPortalUrl}` : "",
      footerDisclosure1Text: values.footerDisclosure1Text,
      footerDisclosure2Text: values.footerDisclosure2Text,
      footerDisclosure3Text: values.footerDisclosure3Text,
      footerDisclosure1Url: values.footerDisclosure1Url
        ? `https://${values.footerDisclosure1Url}`
        : "",
      footerDisclosure2Url: values.footerDisclosure2Url
        ? `https://${values.footerDisclosure2Url}`
        : "",
      footerDisclosure3Url: values.footerDisclosure3Url
        ? `https://${values.footerDisclosure3Url}`
        : "",
    });
    if ("data" in result) {
      message.success("Consumer portal settings updated successfully");
    }
  };

  const handleClientPortalSave = async () => {
    const values = await form.getFieldsValue();
    if (!values.trainingSiteUrl) {
      return;
    }
    const result = await updateClientPortalConfigurations({
      trainingSiteUrl: `https://${values.trainingSiteUrl}`,
    });
    if ("data" in result) {
      message.success("Client portal settings updated successfully");
    }
  };

  const handlePaymentPortalSave = async () => {
    const values = await form.getFieldsValue();
    const result = await updatePaymentPortalConfigurations({
      paymentVerificationMethods: values.paymentVerificationMethods,
    });
    if ("data" in result) {
      message.success("Payment portal settings updated successfully");
    }
  };

  const handleAgencyPortalSave = async () => {
    const values = await form.getFieldsValue();
    const result = await updateAgency({
      paymentDisclosureNotice: values.paymentDisclosureNotice,
    });
    if ("data" in result) {
      message.success("Agency portal settings updated successfully");
    }
  };

  return (
    <Form form={form} layout="vertical">
      <StyledCard title="Agency Portal Settings">
        <StyledFormItem label="Payment Disclosure Notice" name="paymentDisclosureNotice">
          <TextArea
            disabled={!isAgencyUpdateAuthorized}
            rows={6}
            placeholder="Enter payment disclosure notice (shown to users before processing payments)"
          />
        </StyledFormItem>

        {isAgencyUpdateAuthorized && (
          <StyledSaveButtonContainer>
            <Button
              type="primary"
              onClick={handleAgencyPortalSave}
              loading={isAgencySettingsLoading}
            >
              Save Changes
            </Button>
          </StyledSaveButtonContainer>
        )}
      </StyledCard>

      <StyledCard title="Consumer Portal Settings">
        <StyledFormItem
          label="Supported Payment Methods"
          name="supportedPaymentMethods"
          rules={[
            {
              type: "array",
              min: 1,
              message: "Please select at least one payment method",
            },
          ]}
        >
          <Select
            disabled={!isPortalConfigurationsAuthorized}
            mode="multiple"
            allowClear
            maxTagTextLength={20}
            popupMatchSelectWidth={false}
            placeholder="Select payment methods"
            options={constants?.debtorPortalSupportedPaymentMethods?.map((each) => ({
              label: each.display,
              value: each.value,
            }))}
          />
        </StyledFormItem>
        <StyledFormItem label="Signup Disclosure" name="signupDisclosure">
          <TextArea
            disabled={!isPortalConfigurationsAuthorized}
            rows={4}
            placeholder="Enter signup disclosure text..."
          />
        </StyledFormItem>

        <StyledFormItem label="Login Disclosure" name="loginDisclosure">
          <TextArea
            disabled={!isPortalConfigurationsAuthorized}
            rows={4}
            placeholder="Enter login disclosure text..."
          />
        </StyledFormItem>

        <StyledFormItem label="Convenience Fee Disclosure" name="convenienceFeeDisclosure">
          <TextArea
            disabled={!isPortalConfigurationsAuthorized}
            rows={4}
            placeholder="Enter convenience fee disclosure text..."
          />
        </StyledFormItem>

        <StyledFormItem label="Payment Portal URL" name="paymentPortalUrl">
          <Input
            disabled={!isPortalConfigurationsAuthorized}
            addonBefore="https://"
            placeholder="Enter payment portal URL"
          />
        </StyledFormItem>
        <StyledFormItem label="Footer Disclosure URL Label 1" name="footerDisclosure1Text">
          <TextArea
            disabled={!isPortalConfigurationsAuthorized}
            rows={4}
            placeholder="Enter footer disclosure url label"
          />
        </StyledFormItem>
        <StyledFormItem label="Footer Disclosure URL Label 2" name="footerDisclosure2Text">
          <TextArea
            disabled={!isPortalConfigurationsAuthorized}
            rows={4}
            placeholder="Enter footer disclosure url label"
          />
        </StyledFormItem>
        <StyledFormItem label="Footer Disclosure URL Label 3" name="footerDisclosure3Text">
          <TextArea
            disabled={!isPortalConfigurationsAuthorized}
            rows={4}
            placeholder="Enter footer disclosure url label"
          />
        </StyledFormItem>
        <StyledFormItem label="Footer Disclosure URL 1" name="footerDisclosure1Url">
          <Input
            disabled={!isPortalConfigurationsAuthorized}
            addonBefore="https://"
            placeholder="Enter footer disclosure url 1"
          />
        </StyledFormItem>
        <StyledFormItem label="Footer Disclosure URL 2" name="footerDisclosure2Url">
          <Input
            disabled={!isPortalConfigurationsAuthorized}
            addonBefore="https://"
            placeholder="Enter footer disclosure url 2"
          />
        </StyledFormItem>
        <StyledFormItem label="Footer Disclosure URL 3" name="footerDisclosure3Url">
          <Input
            disabled={!isPortalConfigurationsAuthorized}
            addonBefore="https://"
            placeholder="Enter footer disclosure url 3"
          />
        </StyledFormItem>
        {isPortalConfigurationsAuthorized && (
          <StyledSaveButtonContainer>
            <Button
              type="primary"
              onClick={handleConsumerPortalSave}
              loading={isDebtorPortalConfigurationsLoading}
            >
              Save Changes
            </Button>
          </StyledSaveButtonContainer>
        )}
      </StyledCard>

      <StyledCard title="Client Portal Settings">
        <Row gutter={16}>
          <Col span={12}>
            <StyledFormItem label="Training Site URL" name="trainingSiteUrl">
              <Input
                disabled={!isPortalConfigurationsAuthorized}
                addonBefore="https://"
                placeholder="Enter hyperlink"
              />
            </StyledFormItem>
          </Col>
        </Row>
        {isPortalConfigurationsAuthorized && (
          <StyledSaveButtonContainer>
            <Button
              type="primary"
              onClick={handleClientPortalSave}
              loading={isClientPortalConfigurationsLoading}
            >
              Save Changes
            </Button>
          </StyledSaveButtonContainer>
        )}
      </StyledCard>

      <StyledCard title="Payment Portal Settings">
        <StyledFormItem label="Verification Method" name="paymentVerificationMethods">
          <Select
            disabled={!isPortalConfigurationsAuthorized}
            mode="multiple"
            allowClear
            popupMatchSelectWidth={false}
            placeholder="Select a verification method"
            options={constants?.paymentPortalVerificationMethods?.map((method) => ({
              value: method.value,
              label: method.display,
            }))}
          />
        </StyledFormItem>

        {isPortalConfigurationsAuthorized && (
          <StyledSaveButtonContainer>
            <Button
              type="primary"
              onClick={handlePaymentPortalSave}
              loading={isPaymentPortalConfigurationsLoading}
            >
              Save Changes
            </Button>
          </StyledSaveButtonContainer>
        )}
      </StyledCard>
    </Form>
  );
}

export default PortalConfigurations;
